import React, {useState, useEffect, useRef} from 'react';
import {formatPostalCode} from '../utils';
import {CTA, styled} from '@volkswagen-onehub/components-core';

const CenteredDiv = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  max-width: inherit;

  .search-box,
  .search-button {
    padding-bottom: 45px;
  }

  .search-box {
    width: 225px;
  }

  label {
    padding: 0;
  }

  button {
    border-width: 2px !important;
    border-radius: 23px !important;
    padding: 0 2rem 0 !important;
    font-size: 1.5rem !important;
    height: 44px;
    min-width: 144px;
  }

  button[disabled] {
    color: #6a767d !important;
    background-color: #dfe4e8 !important;
    border-color: #dfe4e8 !important;
  }

  .search-label {
    margin: 0;
    //font-family: VWText;
    font-size: 12px;
    line-height: 20px;
    color: #001e50;
  }

  input {
    //font-family: VWText;
    font-size: 12px;
    line-height: 20px;
    height: auto;
    padding: 0.5rem 1rem;
    color: #495057;
    background-color: #fff;
    font-weight: 300;
    will-change: border-color, box-shadow;
    border-radius: 0.375rem;
    box-shadow: none;
    transition: box-shadow 250ms cubic-bezier(0.27, 0.01, 0.38, 1.06),
      border 250ms cubic-bezier(0.27, 0.01, 0.38, 1.06);
    border: none;
    border-radius: 0;
    border-bottom: 1px solid #6a767d;
    width: 100%;
    transition: all 0.2s ease-in-out 0s;

    &:focus {
      outline: 2px solid #4cc7f4;
      outline-offset: 2px;
      border-bottom: 2px solid #001e50;
      box-shadow: none;
      padding: 0.5rem 1rem;
    }
  }
`;

type Props = {
  postalCodeLabel: string;
  postalCodeError: string;
  postalCodePlaceholder: string;
  searchLabel: string;
  invalidZip: boolean;
  inputOnChange: Function;
  tracking: any;
  initialPostalCodeValue?: string | null;
};

const LocatorSearch = (props: Props) => {
  const [postalCode, setPostalCode] = useState('');
  const [enableSearch, setEnableSearch] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    postalCodeLabel,
    postalCodePlaceholder,
    postalCodeError,
    searchLabel,
    invalidZip,
    inputOnChange,
    tracking,
    initialPostalCodeValue
  } = props;

  const _postalCodeHandler = (event: any) => {
    setPostalCode(event.target.value);
    setEnableSearch(!!event.target.value);
  };

  const _cleanUpPostalCode = () => {
    const newPostalCode = formatPostalCode(postalCode, true);
    setPostalCode(newPostalCode);
  };

  const onKeyDown = (e: any) => {
    if (e.key == 'Enter') {
      submitHandler(e);
    }
  };

  const submitHandler = (event: any) => {
    inputOnChange(event, postalCode);
    const data = {
      eventInfo: {
        brand: 'passenger',
      },
      environment: {
        featureAppId: 'TireStorageLocator-D6-CA',
      },
      user: {
        locationZIP: postalCode,
      },
      link: {
        url: window.location.href,
        name: 'Search',
      },
    };

    tracking.track('VWBasic_FindDealerSearch_Click', data);
  };

  useEffect(() => {
    if (invalidZip) {
      const node = inputRef.current;
      if (node !== null) {
        node.focus();
        const y = node.getBoundingClientRect().top + window.scrollY - 100;
        window.scrollTo({top: y, behavior: 'smooth'});
      }
    }
  }, [invalidZip]);

  useEffect(() => {
    if(!initialPostalCodeValue) return;

    setPostalCode(initialPostalCodeValue);
    if(!invalidZip) setEnableSearch(true);
  }, [initialPostalCodeValue]);

  return (
    <div>
      <CenteredDiv className={'search-group'}>
        <div className={'search-box'}>
          <label htmlFor="#searchinput" className="search-label">
            {postalCodeLabel}
          </label>
          <input
            onKeyDown={onKeyDown}
            id="searchinput"
            className="search-input"
            placeholder={postalCodePlaceholder}
            aria-label={
              !invalidZip ? postalCodeLabel : `Error: ${postalCodeError}`
            }
            onChange={_postalCodeHandler}
            aria-describedby="postal-code-error"
            // invalid={invalidZip}
            onBlur={_cleanUpPostalCode}
            value={postalCode}
          />
          {invalidZip ? (
            <label id="postal-code-error" className="search-error">
              {postalCodeError}
            </label>
          ) : (
            <></>
          )}
        </div>
      </CenteredDiv>
      <CenteredDiv className="button-search-row">
        <CTA
          tag="button"
          disabled={!enableSearch}
          onClick={(event: any) => submitHandler(event)}
        >
          {searchLabel}
        </CTA>
      </CenteredDiv>
    </div>
  );
};

export default LocatorSearch;
